import { useMemo, useState, useSyncExternalStore } from 'react';
import { BarChart3, ChevronDown } from '../../icons';
import type { MyTaskDto } from '../../api/tasks';
import { dueDayDiff } from '../ProjectPage/my-task-due';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTasksInsights.css';

/**
 * MyTasksInsights (2026-08-25) — 내 업무의 **시각 요약**. 신규 파일(기존 목록 로직 무수정).
 *
 * 이 화면은 직전에 한 번 걷어냈다(완료 접기·상세열 제거·잡음 배지 제거). 그래서 여기서
 * 더하는 것은 **행을 늘리는 장식이 아니라, 표를 읽어야만 알 수 있던 것을 한눈에 주는 것**만이다.
 *   ① 상태 분포 — 도넛은 완료율 하나만 말한다. 나머지가 어디 있는지는 표를 세어야 했다.
 *   ② 마감 흐름 — "언제 몰리는가" 는 30줄을 훑어야 보이던 정보다.
 *   ③ 프로젝트 쏠림 — "어느 프로젝트가 나를 먹고 있나" 도 마찬가지.
 *
 * 데이터는 전부 이미 받아 둔 목록에서 만든다 — 서버 호출 0.
 *   ⚠ 그래서 **받아 온 범위(기본 100건)** 안의 그림이다. 뒤가 잘렸으면 표 아래 [더 보기] 가
 *     그 사실을 말하고 있고, 더 받으면 그림도 함께 자란다.
 *
 * 색은 화면의 기존 규칙을 그대로 쓴다(설계서 2.1): 완료=초록 · 진행중=파랑 · 검토/임박=주황 ·
 * 지연=빨강 · 할 일=무채색. 새 팔레트를 만들지 않는다 — 같은 뜻에 다른 색이 생기면 그때부터
 * 두 화면이 서로 다른 말을 한다.
 */

/** 마감 흐름에서 보여 줄 앞날 길이. 2주면 "이번 주 / 다음 주" 가 한 눈에 들어온다. */
const DUE_DAYS = 14;

/** 펼침 상태 저장 키 — 개인 UI 선호라 서버에 두지 않는다(사이드바 접힘 등과 같은 규칙). */
const LS_KEY = 'wc.mytasks.insights.v1';

function readOpen(): boolean {
  try {
    const raw = localStorage.getItem(LS_KEY);
    // 처음 온 사람에게는 펼쳐 보여 준다 — 접혀 있으면 있는 줄도 모른다.
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}

function writeOpen(v: boolean): void {
  try {
    localStorage.setItem(LS_KEY, v ? '1' : '0');
  } catch {
    /* localStorage 비활성(프라이빗 모드) — 세션 내 동작은 유지 */
  }
}

interface StatusSlice {
  key: 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE';
  n: number;
  label: string;
}

/**
 * 상태 분포 스택 바 — 히어로 줄 안에 들어가는 **한 줄짜리** 그림.
 *
 * 도넛(완료율)이 말하지 않는 것을 말한다: 남은 일이 아직 손도 안 댄 것인지, 굴러가는 중인지,
 * 검토에서 막혀 있는지. 높이를 늘리지 않으려고 막대 하나 + 범례 한 줄로 눌러 담았다.
 */
export function StatusBar({ items }: { items: MyTaskDto[] }) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);

  const slices = useMemo<StatusSlice[]>(() => {
    const c = { TODO: 0, IN_PROGRESS: 0, REVIEW: 0, DONE: 0 };
    for (const t of items) if (t.status in c) c[t.status as keyof typeof c] += 1;
    return [
      { key: 'TODO', n: c.TODO, label: tProject('project.status.TODO') },
      { key: 'IN_PROGRESS', n: c.IN_PROGRESS, label: tProject('project.status.IN_PROGRESS') },
      { key: 'REVIEW', n: c.REVIEW, label: tProject('project.status.REVIEW') },
      { key: 'DONE', n: c.DONE, label: tProject('project.status.DONE') },
    ];
  }, [items]);

  const total = slices.reduce((a, s) => a + s.n, 0);
  if (total === 0) return null;

  return (
    <div className="mti-statuswrap">
      <div
        className="mti-statusbar"
        role="img"
        aria-label={slices.filter((s) => s.n > 0).map((s) => `${s.label} ${s.n}`).join(', ')}
      >
        {slices.filter((s) => s.n > 0).map((s) => (
          <span
            key={s.key}
            className={`mti-statusseg mti-statusseg--${s.key.toLowerCase()}`}
            style={{ flexGrow: s.n }}
            title={`${s.label} ${s.n}`}
          />
        ))}
      </div>
      <div className="mti-statuslegend" aria-hidden>
        {slices.filter((s) => s.n > 0).map((s) => (
          <span className="mti-legenditem" key={s.key}>
            <i className={`mti-legenddot mti-legenddot--${s.key.toLowerCase()}`} />
            {s.label}
            <b>{s.n}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

interface DueBucket {
  /** 'overdue' | 'none' | 앞으로 n일(0=오늘) */
  kind: 'overdue' | 'none' | 'day';
  offset: number;
  n: number;
  label: string;
  tone: 'over' | 'today' | 'soon' | 'far' | 'none';
}

/** 오늘 기준 offset 일 뒤의 'MM.DD'. */
function dayLabel(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 마감 흐름 — 지연 묶음 + 앞으로 2주 + 미정.
 *
 * 표를 위에서 아래로 읽으면 "언제가 빡센가" 를 알 수 없다. 날짜별 높이로 보면
 * 다음 주 화요일에 네 건이 몰려 있다는 게 그냥 보인다. **완료는 세지 않는다** —
 * 이미 끝난 일의 마감일은 앞으로의 부담이 아니다.
 */
export function DueFlow({ items }: { items: MyTaskDto[] }) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);

  const buckets = useMemo<DueBucket[]>(() => {
    const open = items.filter((t) => t.status !== 'DONE');
    let overdue = 0;
    let none = 0;
    const days = new Array<number>(DUE_DAYS).fill(0);
    for (const t of open) {
      if (!t.dueDate) { none += 1; continue; }
      const diff = dueDayDiff(t.dueDate);
      if (Number.isNaN(diff)) { none += 1; continue; }
      if (diff < 0) { overdue += 1; continue; }
      // 2주 밖은 그리지 않는다 — 지금 손쓸 수 있는 구간이 아니라 막대만 길어진다.
      if (diff < DUE_DAYS) days[diff] = (days[diff] ?? 0) + 1;
    }
    const rows: DueBucket[] = [
      { kind: 'overdue', offset: -1, n: overdue, label: tProject('project.mytask.chip_overdue'), tone: 'over' },
      ...days.map((n, i) => ({
        kind: 'day' as const,
        offset: i,
        n,
        label: i === 0 ? tProject('project.mytask.due_today') : dayLabel(i),
        tone: (i === 0 ? 'today' : i <= 6 ? 'soon' : 'far') as DueBucket['tone'],
      })),
      { kind: 'none', offset: 99, n: none, label: tProject('project.mytask.insights_due_none'), tone: 'none' },
    ];
    return rows;
  }, [items]);

  const max = Math.max(...buckets.map((b) => b.n), 1);
  const totalPlotted = buckets.reduce((a, b) => a + b.n, 0);

  return (
    <div className="mti-card">
      <h3 className="mti-cardtitle">{tProject('project.mytask.insights_due_title')}</h3>
      {totalPlotted === 0 ? (
        <p className="mti-cardempty" role="status">{tProject('project.mytask.insights_empty')}</p>
      ) : (
        <div
          className="mti-flow"
          role="img"
          aria-label={buckets.filter((b) => b.n > 0).map((b) => `${b.label} ${b.n}`).join(', ')}
        >
          {buckets.map((b) => (
            <div
              className={`mti-flowcol${b.n === 0 ? ' mti-flowcol--zero' : ''}`}
              key={`${b.kind}-${b.offset}`}
              title={`${b.label} · ${b.n}`}
            >
              <span className="mti-flowbarwrap" aria-hidden>
                {/* 0 건도 자리는 남긴다 — 빈 날이 보여야 '몰린 날' 이 도드라진다. */}
                <span
                  className={`mti-flowbar mti-flowbar--${b.tone}`}
                  style={{ height: b.n === 0 ? 2 : `${Math.max((b.n / max) * 100, 12)}%` }}
                />
              </span>
              {b.n > 0 && <b className="mti-flownum" aria-hidden>{b.n}</b>}
              <span className={`mti-flowlabel${b.kind === 'day' && b.offset === 0 ? ' mti-flowlabel--today' : ''}`}>
                {b.kind === 'day' && b.offset > 0 && b.offset % 2 === 1 ? '' : b.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 프로젝트 쏠림 — 미완료 기준 상위 5개.
 *
 * "내 일이 30건" 보다 "그중 11건이 한 프로젝트" 가 실제로 쓰이는 정보다.
 * 막대를 누르면 그 프로젝트로 목록을 좁힌다(검색창에 이름을 넣는 것과 같은 효과).
 */
export function ProjectLoad({ items, onPick }: { items: MyTaskDto[]; onPick?: (name: string) => void }) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);

  const rows = useMemo(() => {
    const m = new Map<string, { name: string; n: number; overdue: number }>();
    for (const t of items) {
      if (t.status === 'DONE') continue;
      const name = t.project?.name ?? '-';
      const cur = m.get(name) ?? { name, n: 0, overdue: 0 };
      cur.n += 1;
      if (t.dueDate && dueDayDiff(t.dueDate) < 0) cur.overdue += 1;
      m.set(name, cur);
    }
    return [...m.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name)).slice(0, 5);
  }, [items]);

  const max = Math.max(...rows.map((r) => r.n), 1);

  return (
    <div className="mti-card">
      <h3 className="mti-cardtitle">{tProject('project.mytask.insights_project_title')}</h3>
      {rows.length === 0 ? (
        <p className="mti-cardempty" role="status">{tProject('project.mytask.insights_empty')}</p>
      ) : (
        <ul className="mti-loadlist">
          {rows.map((r) => (
            <li className="mti-loadrow" key={r.name}>
              <button
                type="button"
                className="mti-loadbtn"
                onClick={() => onPick?.(r.name)}
                data-tip={tProject('project.mytask.insights_project_pick', { name: r.name })}
              >
                <span className="mti-loadname">{r.name}</span>
                <span className="mti-loadtrack" aria-hidden>
                  <span className="mti-loadfill" style={{ width: `${(r.n / max) * 100}%` }} />
                  {/* 지연분은 같은 막대 안에서 색만 달리한다 — 막대를 하나 더 만들면 줄이 두 배가 된다. */}
                  {r.overdue > 0 && (
                    <span className="mti-loadfill mti-loadfill--over" style={{ width: `${(r.overdue / max) * 100}%` }} />
                  )}
                </span>
                <b className="mti-loadnum">
                  {r.n}
                  {r.overdue > 0 && <i className="mti-loadover">{tProject('project.mytask.insights_overdue_of', { n: String(r.overdue) })}</i>}
                </b>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 접이식 '한눈에 보기' — 마감 흐름 + 프로젝트 쏠림.
 * 기본은 펼침이되, 접으면 그 선택을 기억한다(표만 보고 싶은 사람의 화면을 뺏지 않는다).
 */
export function MyTasksInsights({ items, onPickProject }: {
  items: MyTaskDto[];
  onPickProject?: (name: string) => void;
}) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const [open, setOpen] = useState(readOpen);

  return (
    <section className="mti">
      <button
        type="button"
        className={`mti-toggle${open ? ' mti-toggle--on' : ''}`}
        aria-expanded={open}
        onClick={() => { setOpen((v) => { writeOpen(!v); return !v; }); }}
      >
        <BarChart3 size={14} aria-hidden />
        {tProject('project.mytask.insights_heading')}
        <ChevronDown size={14} aria-hidden className="mti-togglechevron" />
      </button>

      {open && (
        <div className="mti-grid">
          <DueFlow items={items} />
          <ProjectLoad items={items} onPick={onPickProject} />
        </div>
      )}
    </section>
  );
}
